/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

// REDUX Imports
import {
  getGSTDetails,
  verifyGST,
  resetSelectedGst,
  resetVerifyGST,
} from "../../../redux/features/sharedResources/sharedResources";

// UTILS Imports
import Validation from "../../../utils/validation";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import formatGSTDate from "../../../utils/formatGSTDate";
import TEXT from "../../../utils/globalText";

// COMPONENTS Imports
import Loader from "../../../components/Loader/Index";
import PrimaryButton from "../../../components/_utils/CommonButtons/PrimaryButton";
import TopNavSmallButton from "../../../components/_utils/CommonButtons/TopNavSmallButton";
import SearchBar from "../../../components/_utils/CommonSearchComponents/SearchBar";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent";
import CommonToast from "../../../components/_utils/CommonToast";
import TitleSubtitlePair from "../../../components/_utils/TitleSubtitlePair";
import SwitcherTabs from "../../../components/_utils/CommonButtons/SwitcherTabs";
import GeoMapLeaflet from "../../../components/_utils/GeoMapLeaflet";
import LargePopupRedirect from "../../../components/_utils/CommonPopups/CommonLargePopupRedirect";
import CommonContainerCard from "../../../components/_utils/CommonContainerCard";
import PanVerification from "../PanVerification";
import useServiceChecker from "../../../components/ServiceChecker";
import ICONS from "../../../assets/Icons";
import useRestrictedAction from "../../../hooks/useRestrictedAction";

const GSTVerification = ({ gst_number, popup }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef("");
  const location = useLocation();
  const [ensureAny] = useServiceChecker();
  const gstNumber =
    typeof location.state === "string"
      ? location.state
      : location.state?.gstNumber;
  // Redux State
  const {
    gstDetailsData,
    gstDetailsLoading,
    gstVerificationLoading,
    selectedGstData,
  } = useSelector((store) => store.sharedResources);
  const services = useSelector((state) => state.refreshToken.services);

  const [gstNo, setGstNo] = useState("");
  const [prevGstNo, setPrevGstNo] = useState("");
  const [hasFetched, setHasFetched] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [resultStatus, setResultStatus] = useState(null);
  const [showPanPopup, setPanShowPopup] = useState(false);
  const handleGSTInput = (value) => {
    const GstValue = value.toUpperCase();
    const updateData = GstValue.replace(/[^A-Z0-9]/g, "");
    if (updateData.length <= GlobalConfiguration.gstLength) {
      setGstNo(updateData);
    }
  };

  const performGstFetch = async (targetGst) => {
    if (!targetGst || targetGst.length === GlobalConfiguration?.lengthCheckZero)
      return;

    setHasFetched(true);
    setIsFetching(true);

    setPrevGstNo(targetGst);

    if (!Validation.validateGstWithRegex(targetGst)) {
      setResultStatus("error");
      setIsFetching(false);
      dispatch(resetVerifyGST());
      CommonToast({
        text: "Invalid GSTIN!",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });
      return;
    }
    setPrevGstNo(targetGst);
    let response = await dispatch(getGSTDetails({ search_string: targetGst }));

    if (response?.payload?.gstin_details === undefined) {
      response = await dispatch(verifyGST({ search_string: targetGst }));
    }

    if (response?.payload?.status === true) {
      setResultStatus("success");
    } else {
      setResultStatus("not_found");
    }
    setIsFetching(false);
  };

  const { showRestrictedAction, RestrictedActionPopup } = useRestrictedAction();

  const actionRestricted = () => {
    showRestrictedAction({
      path: "/settings/GST_verification",
      state: {
        gstNumber:
          gstDetailsData?.gstin_details?.gstin_no || gst_number || gstNo,
      },
    });
  };

  const handleUpdateDetails = async () => {
    if (popup) {
      actionRestricted();
      return;
    }
    CommonToast({
      text: "Updating details.",
      icon: ICONS?.LOADER_GIF,
      variant: "var(--processing)",
      buttonNeeded: false,
    });

    const response = await dispatch(
      verifyGST({
        search_string: gstDetailsData?.gstin_details?.gstin_no,
      }),
    );

    toast.dismiss();
    if (response?.payload?.status === true) {
      CommonToast({
        text: "Details updated successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
      });
    } else {
      CommonToast({
        text: "Failed to update.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });
    }
  };

  const renderGstDetails = () => (
    <div className={`${popup ? "mb-margin-timeline-vertical" : ""}`}>
      {gstDetailsLoading ? (
        <div className="w-width-full flex justify-center items-center m-auto">
          <Loader />
        </div>
      ) : gstDetailsData?.gstin_details ? (
        <>
          <div className="w-auto bg-primary-nav Qmd:pt-padding-base">
            <div className="max-w-tab-width-normal mx-auto Qlg:px-padding-base">
              <div
                className={`flex justify-between items-center border-0 Qmd:border-b border-primary-bg p-mobile-padding-l Qmd:py-padding-base Qmd:pl-padding-md Qlg:pl-padding-base Qmd:pr-padding-base`}
              >
                <TitleSubtitlePair
                  title={gstDetailsData?.gstin_details?.business_name}
                  subtitle="Company name"
                />
                <div className="hidden Qmd:flex">
                  <TopNavSmallButton
                    enabledBgColor="var(--primary-nav-act)"
                    enabledTextColor="var(--text-primary)"
                    hoverOption="white"
                    isDisabled={popup || gstVerificationLoading}
                    handleFunction={handleUpdateDetails}
                    icon={ICONS?.REFRESH_BALANCE}
                    btnText={
                      <>
                        Refresh <br /> details
                      </>
                    }
                  />
                </div>
              </div>
              <div className="mx-mobile-margin-l border-b border-solid border-primary-bg Qmd:hidden"></div>
              <div className="grid grid-cols-1 Qmd:grid-cols-3 items-center border-0 Qmd:border-b border-primary-bg py-mobile-padding-l Qmd:py-padding-base Qlg:py-padding-sm">
                <div className="Qmd:border-r border-primary-bg pb-mobile-padding-l px-mobile-padding-l Qmd:pb-padding-base Qmd:px-padding-md Qlg:pl-padding-base">
                  <TitleSubtitlePair
                    title={gstDetailsData?.gstin_details?.gstin_no}
                    subtitle="GST number"
                  />
                </div>
                <div className="mx-mobile-margin-l border-b border-solid border-primary-bg Qmd:hidden"></div>
                <div className="flex Qlg:mx-margin-base p-padding-button-s Qlg:my-margin-base Qmd:px-padding-sm">
                  <TitleSubtitlePair
                    title={gstDetailsData?.gstin_details?.pan_no}
                    subtitle="PAN number"
                    showArrow={true}
                    hoverOption="white"
                    isDisabled={popup}
                    onClick={() => {
                      if (popup) {
                        actionRestricted();
                        return;
                      }
                      if (ensureAny(["VPAN"]).status) {
                        setPanShowPopup(true);
                      } else {
                        navigate("/feature_not_assigned", {
                          state: {
                            unavailableService: "VPAN",
                          },
                        });
                      }
                    }}
                  />
                </div>
                <div className="mx-mobile-margin-l border-b border-solid border-primary-bg Qmd:hidden"></div>
                <div className="Qmd:border-l border-primary-bg px-mobile-padding-l Qmd:px-padding-md Qmd:py-padding-base pt-mobile-padding-l">
                  <TitleSubtitlePair
                    title={formatGSTDate(
                      gstDetailsData?.gstin_details?.date_of_registration,
                    )}
                    subtitle="Estd. on"
                  />
                </div>
              </div>
              <div className="mx-mobile-margin-l border-b border-solid border-primary-bg Qmd:hidden"></div>
              <div className="grid grid-cols-1 Qmd:grid-cols-3 items-center Qmd:py-padding-md">
                <div className="Qmd:border-r border-solid border-primary-bg p-mobile-padding-l Qmd:px-padding-md Qlg:pl-padding-base Qmd:py-padding-base">
                  <TitleSubtitlePair
                    title={
                      gstDetailsData?.gstin_details?.constitution_of_business
                    }
                    subtitle="Class of company"
                  />
                </div>
                <div className="mx-mobile-margin-l border-b border-solid border-primary-bg Qmd:hidden"></div>
                <div className="Qmd:border-r Qmd:border-b-border-base border-primary-bg p-mobile-padding-l Qmd:px-padding-md Qmd:py-padding-base">
                  <TitleSubtitlePair
                    title={gstDetailsData?.gstin_details?.taxpayer_type}
                    subtitle="Taxpayer type"
                  />
                </div>
                <div className="mx-mobile-margin-l border-b border-solid border-primary-bg Qmd:hidden"></div>
                <div className="p-mobile-padding-l Qmd:px-padding-md Qmd:py-padding-base">
                  <TitleSubtitlePair
                    title={gstDetailsData?.gstin_details?.status}
                    subtitle="Company status"
                    titleColor={
                      gstDetailsData?.gstin_details?.status === "Active"
                        ? "settled"
                        : "failed"
                    }
                    isBold={true}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-primary-nav-act block Qmd:hidden mt-margin-md">
            <p className="bg-primary-card px-mobile-padding-l py-padding-sm text-sm font-semibold text-text-primary">
              Quick Actions
            </p>
            <div className="flex justify-center ">
              <TopNavSmallButton
                enabledBgColor="var(--primary-nav-act)"
                enabledTextColor="var(--text-primary)"
                hoverOption="white"
                isDisabled={popup || gstVerificationLoading}
                fullWidthRequired={true}
                handleFunction={handleUpdateDetails}
                icon={ICONS?.REFRESH_BALANCE}
                btnText={<span className="text-xs">Refresh details</span>}
              />
            </div>
          </div>

          <div
            className={`max-w-tab-width-normal w-width-full mx-auto px-padding-base pt-padding-md Qmd:pt-padding-base ${popup && "pb-padding-md"}`}
          >
            <div className="Qmd:py-padding-md pb-padding-md pl-mobile-padding-l Qmd:pl-padding-md Qlg:pl-padding-base">
              <SwitcherTabs
                tabs={[
                  {
                    label: "Address",
                    key: "Address",
                    handleFunction: () => {},
                  },
                ]}
              />
            </div>
            <CommonContainerCard header={TEXT?.CORPORATE_ADDRESS}>
              <div className="flex flex-col gap-gap-md md:flex-row Qmd:flex-row items-start justify-between ">
                <div className="flex flex-col gap-y-gap-xs">
                  <p className="text-text-primary text-font-sm font-normal">
                    {gstDetailsData?.gstin_details?.address || "N/A"}
                  </p>
                </div>

                <div className="flex justify-center Qmd:justify-center md:justify-end Qmd:max-w-max-map-leaflet-width w-width-full Qmd:flex-shrink-0 flex-shrink z-z-index-content">
                  <GeoMapLeaflet
                    address={gstDetailsData?.gstin_details?.address}
                    owner="cin_details"
                    enableGeocoding={false}
                  />
                </div>
              </div>
            </CommonContainerCard>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );

  const isGSTLengthValid = gstNo?.length === GlobalConfiguration?.gstLength;
  const hasGSTLoading = !!gstDetailsLoading;

  const isFetchButtonDisabled =
    !isGSTLengthValid || hasGSTLoading || isFetching || gstNo === prevGstNo;

  useEffect(() => {
    if (gstNo?.length === GlobalConfiguration?.lengthCheckZero) {
      setGstNo("");
      setPrevGstNo("");
      setHasFetched(false);
      setIsFetching(false);
    }
  }, [gstNo, isFetching]);

  useEffect(() => {
    if (services && !services?.includes("VGSTIN")) {
      navigate("/feature_not_assigned", {
        state: { unavailableService: ["VGSTIN"] },
      });
    }

    if (popup && gst_number) {
      performGstFetch(gst_number, false); // popup mode - no toast
    } else if (gstNumber && Validation.validateGstWithRegex(gstNumber)) {
      setGstNo(gstNumber);
      performGstFetch(gstNumber);
    } else if (selectedGstData) {
      setGstNo(selectedGstData);
    }

    return () => {
      dispatch(resetSelectedGst());
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter" && !isFetchButtonDisabled) {
        performGstFetch(gstNo);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [gstNo, isFetchButtonDisabled]);

  return (
    <div
      className={`flex flex-col min-h-screen mt-margin-base bg-primary-bg overflow-y-auto ${popup ? "h-screen pb-padding-base" : "pb-padding-md"} `}
    >
      <div
        className={`bg-primary-nav shadow-box-shadow-bottom fixed top-top-base left-left-base z-z-index-tooltip w-width-full block ${popup ? "hidden" : "Qsmd:hidden"}`}
      >
        <TopNavComponent
          isFormattingNotRequired={true}
          topNavHeader="GST Verification"
        />
      </div>

      <>{RestrictedActionPopup}</>
      {showPanPopup && (
        <div>
          <LargePopupRedirect
            headerTitle="PAN Details"
            isfullViewrequired={true}
            fullviewhandleFunction={() =>
              navigate(`/settings/PAN_verification`, {
                state: gstDetailsData?.gstin_details?.pan_no,
              })
            }
            closeiconhandleFunction={() => setPanShowPopup(false)}
          >
            <PanVerification
              pan_no={gstDetailsData?.gstin_details?.pan_no}
              popup={true}
            />
          </LargePopupRedirect>
        </div>
      )}
      {!popup && (
        <>
          <div className="hidden Qsmd:flex mobile-navbar-height Qsmd:min-h-navbar-breadcrumb-height bg-primary-nav"></div>
          <div className="p-mobile-padding-l Qmd:p-padding-md mt-margin-xl Qsmd:mt-margin-base w-width-full bg-primary-nav opacity-base-opacity flex flex-col justify-center items-center z-z-index-header shadow-box-shadow-bottom">
            <SearchBar
              inActiveVariantNeeded
              searchText={gstNo}
              searchRef={inputRef}
              placeholder="Enter GST number"
              onInput={handleGSTInput}
              btnText="Fetch"
              onClick={() => {
                if (isFetchButtonDisabled) return;
                performGstFetch(gstNo);
              }}
              isBtnDisable={isFetchButtonDisabled}
              txtColor={
                !isFetchButtonDisabled
                  ? "var(--primary-bg)"
                  : "var(--text-primary)"
              }
              btnColor={
                !isFetchButtonDisabled
                  ? "var(--text-primary)"
                  : "var(--text-disabled)"
              }
            />
          </div>
        </>
      )}

      {gstDetailsLoading || isFetching ? (
        <div className="w-width-full flex justify-center items-center m-auto">
          <Loader />
        </div>
      ) : resultStatus === "not_found" || resultStatus === "error" ? (
        <div className="flex flex-col flex-1 items-center justify-center">
          <img
            src={ICONS?.NO_DATA_FOUND}
            alt="notfound"
            className="px-padding-md w-graphic-width-md h-graphic-rectangle-height-md"
          />
          <p className="text-font-sm text-text-primary text-center my-margin-sm">
            No data found.
          </p>
          <PrimaryButton
            btnText="Clear parameters"
            handleFunction={() => {
              setGstNo("");
              setPrevGstNo("");
              setHasFetched(false);
              setIsFetching(false);
              setResultStatus(null);
              dispatch(resetVerifyGST());
            }}
            enabledBgColor="var(--primary-act)"
            enabledTextColor="var(--text-primary)"
          />
        </div>
      ) : resultStatus === "success" ? (
        renderGstDetails()
      ) : !hasFetched ? (
        <div className="w-width-full flex flex-col justify-center items-center mx-auto my-auto">
          <img
            src={ICONS?.IFSC_VERIFICATION_GRAPHICS}
            alt="bgDefault"
            className="px-padding-md h-graphic-square-height-md w-graphic-width-md"
          />
          <p className="text-font-sm text-text-primary text-center mt-margin-sm">
            Enter GST number in the search bar, to
            <br />
            fetch the verified GST details.
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default GSTVerification;
